import React, { useState, useEffect } from 'react';
import SMSF_RATES_CONFIG from './SMSF_Rates_Config.js';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';

const SMSFAdvancedAnalytics = ({ smsfState, setSmsfState }) => {
    const [activeView, setActiveView] = useState('performance');
    const [timeframe, setTimeframe] = useState('1Y');
    const [benchmarkData, setBenchmarkData] = useState({});
    const [portfolioMetrics, setPortfolioMetrics] = useState({});
    const [showAssetAllocationModal, setShowAssetAllocationModal] = useState(false);
    const [editingAsset, setEditingAsset] = useState(null);

    // Performance benchmarking data
    const performanceBenchmarks = {
        '1Y': {
            smsf: { return: 8.5, volatility: 12.3, sharpe: 0.69, maxDrawdown: -8.2 },
            industry: { return: 7.2, volatility: 11.8, sharpe: 0.61, maxDrawdown: -7.5 },
            balanced: { return: 6.8, volatility: 10.2, sharpe: 0.67, maxDrawdown: -6.8 },
            growth: { return: 9.1, volatility: 14.5, sharpe: 0.63, maxDrawdown: -11.2 }
        },
        '3Y': {
            smsf: { return: 9.2, volatility: 13.1, sharpe: 0.70, maxDrawdown: -12.8 },
            industry: { return: 7.8, volatility: 12.5, sharpe: 0.62, maxDrawdown: -11.5 },
            balanced: { return: 7.1, volatility: 11.2, sharpe: 0.63, maxDrawdown: -9.8 },
            growth: { return: 9.8, volatility: 15.8, sharpe: 0.62, maxDrawdown: -16.5 }
        },
        '5Y': {
            smsf: { return: 8.8, volatility: 12.9, sharpe: 0.68, maxDrawdown: -15.2 },
            industry: { return: 7.5, volatility: 12.1, sharpe: 0.62, maxDrawdown: -13.8 },
            balanced: { return: 6.9, volatility: 10.8, sharpe: 0.64, maxDrawdown: -12.1 },
            growth: { return: 9.3, volatility: 15.2, sharpe: 0.61, maxDrawdown: -18.9 }
        }
    };

    // Asset allocation analysis - pulled from SMSF state
    const assetAllocation = {
        australian_shares: { 
            target: smsfState.assetAllocationTargets?.australian_shares || 25, 
            actual: smsfState.assetAllocationActual?.australian_shares || 0, 
            value: smsfState.assetValues?.australian_shares || 0, 
            performance: smsfState.assetPerformance?.australian_shares || 0 
        },
        international_shares_ex_us: { 
            target: smsfState.assetAllocationTargets?.international_shares_ex_us || 20, 
            actual: smsfState.assetAllocationActual?.international_shares_ex_us || 0, 
            value: smsfState.assetValues?.international_shares_ex_us || 0, 
            performance: smsfState.assetPerformance?.international_shares_ex_us || 0 
        },
        us_shares: { 
            target: smsfState.assetAllocationTargets?.us_shares || 15, 
            actual: smsfState.assetAllocationActual?.us_shares || 0, 
            value: smsfState.assetValues?.us_shares || 0, 
            performance: smsfState.assetPerformance?.us_shares || 0 
        },
        property: { 
            target: smsfState.assetAllocationTargets?.property || 25, 
            actual: smsfState.assetAllocationActual?.property || 0, 
            value: smsfState.assetValues?.property || 0, 
            performance: smsfState.assetPerformance?.property || 0 
        },
        fixed_income: { 
            target: smsfState.assetAllocationTargets?.fixed_income || 10, 
            actual: smsfState.assetAllocationActual?.fixed_income || 0, 
            value: smsfState.assetValues?.fixed_income || 0, 
            performance: smsfState.assetPerformance?.fixed_income || 0 
        },
        alternatives: { 
            target: smsfState.assetAllocationTargets?.alternatives || 5, 
            actual: smsfState.assetAllocationActual?.alternatives || 0, 
            value: smsfState.assetValues?.alternatives || 0, 
            performance: smsfState.assetPerformance?.alternatives || 0 
        }
    };

    // Risk metrics
    const riskMetrics = {
        var_95: -2.8, // Value at Risk 95%
        var_99: -4.2, // Value at Risk 99%
        expected_shortfall: -3.5,
        beta: 1.12,
        tracking_error: 2.8,
        information_ratio: 0.45,
        sortino_ratio: 1.15
    };

    // Attribution analysis
    const attributionAnalysis = {
        asset_allocation: {
            contribution: 0.8,
            percentage: 45
        },
        security_selection: {
            contribution: 1.2,
            percentage: 35
        },
        currency_hedging: {
            contribution: 0.3,
            percentage: 20
        }
    };

    // Stress testing scenarios
    const stressTests = [
        {
            scenario: '2008 Financial Crisis',
            description: 'Global financial crisis impact',
            portfolio_return: -18.5,
            benchmark_return: -22.3,
            outperformance: 3.8
        },
        {
            scenario: 'COVID-19 Market Crash',
            description: 'Pandemic market volatility',
            portfolio_return: -12.8,
            benchmark_return: -16.2,
            outperformance: 3.4
        },
        {
            scenario: 'Interest Rate Shock',
            description: 'Rapid interest rate increases',
            portfolio_return: -8.2,
            benchmark_return: -9.5,
            outperformance: 1.3
        },
        {
            scenario: 'Property Market Correction',
            description: 'Real estate market downturn',
            portfolio_return: -6.5,
            benchmark_return: -4.8,
            underperformance: -1.7
        }
    ];

    // Monte Carlo simulation results
    const monteCarloResults = {
        scenarios: 10000,
        time_horizon: 10,
        results: {
            '10th_percentile': 850000,
            '25th_percentile': 1200000,
            '50th_percentile': 1650000,
            '75th_percentile': 2200000,
            '90th_percentile': 2800000,
            'probability_of_shortfall': 15
        }
    };

    // Calculate portfolio metrics
    const calculatePortfolioMetrics = () => {
        const currentData = performanceBenchmarks[timeframe];
        const smsfData = currentData.smsf;
        
        return {
            total_return: smsfData.return,
            annualized_return: smsfData.return,
            volatility: smsfData.volatility,
            sharpe_ratio: smsfData.sharpe,
            max_drawdown: smsfData.maxDrawdown,
            calmar_ratio: Math.abs(smsfData.return / smsfData.maxDrawdown),
            sortino_ratio: smsfData.return / (smsfData.volatility * 0.8), // Simplified calculation
            information_ratio: 0.45,
            tracking_error: 2.8,
            beta: 1.12
        };
    };

    useEffect(() => {
        setBenchmarkData(performanceBenchmarks[timeframe]);
        setPortfolioMetrics(calculatePortfolioMetrics());
    }, [timeframe]);

    const renderPerformanceBenchmarking = () => (
        <div className="space-y-6">
            {/* Performance Summary */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Performance Summary ({timeframe})</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">
                            {benchmarkData.smsf?.return?.toFixed(1)}%
                        </div>
                        <div className="text-sm text-blue-700">Total Return</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                            {benchmarkData.smsf?.sharpe?.toFixed(2)}
                        </div>
                        <div className="text-sm text-green-700">Sharpe Ratio</div>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                        <div className="text-2xl font-bold text-orange-600">
                            {benchmarkData.smsf?.volatility?.toFixed(1)}%
                        </div>
                        <div className="text-sm text-orange-700">Volatility</div>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                        <div className="text-2xl font-bold text-red-600">
                            {benchmarkData.smsf?.maxDrawdown?.toFixed(1)}%
                        </div>
                        <div className="text-sm text-red-700">Max Drawdown</div>
                    </div>
                </div>
            </div>

            {/* Benchmark Comparison */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Benchmark Comparison</h3>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left py-3 px-4">Strategy</th>
                                <th className="text-right py-3 px-4">Return</th>
                                <th className="text-right py-3 px-4">Volatility</th>
                                <th className="text-right py-3 px-4">Sharpe Ratio</th>
                                <th className="text-right py-3 px-4">Max Drawdown</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(benchmarkData).map(([strategy, data]) => (
                                <tr key={strategy} className={`border-b border-gray-100 ${
                                    strategy === 'smsf' ? 'bg-blue-50' : ''
                                }`}>
                                    <td className="py-3 px-4 font-medium">
                                        {strategy === 'smsf' ? 'Your SMSF' :
                                         strategy === 'industry' ? 'Industry Average' :
                                         strategy === 'balanced' ? 'Balanced Fund' :
                                         'Growth Fund'}
                                    </td>
                                    <td className={`py-3 px-4 text-right font-medium ${
                                        data.return > benchmarkData.smsf?.return ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                        {(data.return || 0).toFixed(1)}%
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                        {(data.volatility || 0).toFixed(1)}%
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                        {(data.sharpe || 0).toFixed(2)}
                                    </td>
                                    <td className="py-3 px-4 text-right text-red-600">
                                        {(data.maxDrawdown || 0).toFixed(1)}%
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Performance Attribution */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Performance Attribution</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {Object.entries(attributionAnalysis).map(([factor, data]) => (
                        <div key={factor} className="text-center p-4 border border-gray-200 rounded-lg">
                            <h4 className="font-semibold text-gray-800 mb-2">
                                {factor.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </h4>
                            <div className="text-2xl font-bold text-blue-600 mb-1">
                                +{(data.contribution || 0).toFixed(1)}%
                            </div>
                            <div className="text-sm text-gray-600">
                                {data.percentage}% of total return
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderRiskAnalysis = () => (
        <div className="space-y-6">
            {/* Risk Metrics */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Risk Metrics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                        <div className="text-xl font-bold text-red-600">
                            {riskMetrics.var_95.toFixed(1)}%
                        </div>
                        <div className="text-sm text-red-700">VaR (95%)</div>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                        <div className="text-xl font-bold text-orange-600">
                            {riskMetrics.var_99.toFixed(1)}%
                        </div>
                        <div className="text-sm text-orange-700">VaR (99%)</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-xl font-bold text-blue-600">
                            {riskMetrics.beta.toFixed(2)}
                        </div>
                        <div className="text-sm text-blue-700">Beta</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-xl font-bold text-green-600">
                            {riskMetrics.sortino_ratio.toFixed(2)}
                        </div>
                        <div className="text-sm text-green-700">Sortino Ratio</div>
                    </div>
                </div>
            </div>

            {/* Stress Testing */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Stress Testing Scenarios</h3>
                <div className="space-y-4">
                    {stressTests.map((test, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h4 className="font-semibold text-gray-800">{test.scenario}</h4>
                                    <p className="text-sm text-gray-600">{test.description}</p>
                                </div>
                                <div className="text-right">
                                        <div className={`text-lg font-bold ${
                                            test.outperformance > 0 ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                            {test.outperformance > 0 ? '+' : ''}{(test.outperformance || 0).toFixed(1)}%
                                        </div>
                                    <div className="text-sm text-gray-500">vs Benchmark</div>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="text-gray-500">Portfolio Return:</span>
                                    <span className={`ml-2 font-medium ${
                                        test.portfolio_return >= 0 ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                        {(test.portfolio_return || 0).toFixed(1)}%
                                    </span>
                                </div>
                                <div>
                                    <span className="text-gray-500">Benchmark Return:</span>
                                    <span className={`ml-2 font-medium ${
                                        test.benchmark_return >= 0 ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                        {(test.benchmark_return || 0).toFixed(1)}%
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Risk-Adjusted Returns */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Risk-Adjusted Returns</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-xl font-bold text-green-600">
                            {portfolioMetrics.sharpe_ratio?.toFixed(2)}
                        </div>
                        <div className="text-sm text-green-700">Sharpe Ratio</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-xl font-bold text-blue-600">
                            {portfolioMetrics.sortino_ratio?.toFixed(2)}
                        </div>
                        <div className="text-sm text-blue-700">Sortino Ratio</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <div className="text-xl font-bold text-purple-600">
                            {portfolioMetrics.calmar_ratio?.toFixed(2)}
                        </div>
                        <div className="text-sm text-purple-700">Calmar Ratio</div>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                        <div className="text-xl font-bold text-orange-600">
                            {portfolioMetrics.information_ratio?.toFixed(2)}
                        </div>
                        <div className="text-sm text-orange-700">Information Ratio</div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderAssetAllocation = () => (
        <div className="space-y-6">
            {/* Asset Allocation Overview */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Asset Allocation Analysis</h3>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setShowAssetAllocationModal(true)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                        >
                            Edit Allocation
                        </button>
                        <button
                            onClick={() => {
                                // Auto-calculate actual allocation based on current portfolio values
                                const totalValue = Object.values(assetAllocation).reduce((sum, asset) => sum + asset.value, 0);
                                if (totalValue > 0) {
                                    const newActual = {};
                                    Object.keys(assetAllocation).forEach(key => {
                                        newActual[key] = ((assetAllocation[key].value / totalValue) * 100).toFixed(1);
                                    });
                                    setSmsfState(prev => ({
                                        ...prev,
                                        assetAllocationActual: newActual
                                    }));
                                }
                            }}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                        >
                            Calculate Actual
                        </button>
                    </div>
                </div>
                
                {/* Equity Breakdown Summary */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">Equity Allocation Breakdown</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div className="text-center">
                            <div className="font-medium text-blue-700">Australian Shares</div>
                            <div className="text-lg font-bold">{assetAllocation.australian_shares.actual}%</div>
                        </div>
                        <div className="text-center">
                            <div className="font-medium text-blue-700">International ex-US</div>
                            <div className="text-lg font-bold">{assetAllocation.international_shares_ex_us.actual}%</div>
                        </div>
                        <div className="text-center">
                            <div className="font-medium text-blue-700">US Shares</div>
                            <div className="text-lg font-bold">{assetAllocation.us_shares.actual}%</div>
                        </div>
                    </div>
                    <div className="text-center mt-2 text-sm text-blue-600">
                        Total Equity Allocation: {assetAllocation.australian_shares.actual + assetAllocation.international_shares_ex_us.actual + assetAllocation.us_shares.actual}%
                    </div>
                </div>

                <div className="space-y-4">
                    {Object.entries(assetAllocation).map(([asset, data]) => {
                        // Get color based on asset type
                        const getAssetColor = (assetType) => {
                            if (assetType.includes('australian')) return 'bg-green-600';
                            if (assetType.includes('international')) return 'bg-blue-600';
                            if (assetType.includes('us_shares')) return 'bg-purple-600';
                            if (assetType.includes('property')) return 'bg-orange-600';
                            if (assetType.includes('fixed_income')) return 'bg-yellow-600';
                            if (assetType.includes('alternatives')) return 'bg-pink-600';
                            return 'bg-gray-600';
                        };

                        return (
                            <div key={asset} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <h4 className="font-semibold text-gray-800">
                                            {asset.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                            Target: {data.target}% | Actual: {data.actual}%
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-lg font-bold text-green-600">
                                            {(data.performance || 0).toFixed(1)}%
                                        </div>
                                        <div className="text-sm text-gray-500">Performance</div>
                                    </div>
                                </div>
                                
                                <div className="mb-3">
                                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                                        <span>Allocation</span>
                                        <span>{data.actual}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div 
                                            className={`${getAssetColor(asset)} h-2 rounded-full`}
                                            style={{ width: `${data.actual}%` }}
                                        ></div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-500">Value:</span>
                                        <span className="ml-2 font-medium">${data.value.toLocaleString()}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Variance:</span>
                                        <span className={`ml-2 font-medium ${
                                            data.actual > data.target ? 'text-red-600' : 'text-green-600'
                                        }`}>
                                            {((data.actual || 0) - (data.target || 0)).toFixed(1)}%
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Rebalancing Recommendations */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Rebalancing Recommendations</h3>
                <div className="space-y-3">
                    {Object.entries(assetAllocation)
                        .filter(([asset, data]) => Math.abs(data.actual - data.target) > 2)
                        .map(([asset, data]) => (
                            <div key={asset} className="p-3 border border-gray-200 rounded-lg">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h4 className="font-semibold text-gray-800">
                                            {asset.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                            Current: {data.actual}% | Target: {data.target}%
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            Variance: {((data.actual - data.target) > 0 ? '+' : '') + (data.actual - data.target).toFixed(1)}%
                                        </p>
                                    </div>
                                    <div className={`px-3 py-1 text-xs rounded ${
                                        data.actual > data.target 
                                            ? 'bg-red-100 text-red-800' 
                                            : 'bg-green-100 text-green-800'
                                    }`}>
                                        {data.actual > data.target ? 'REDUCE' : 'INCREASE'}
                                    </div>
                                </div>
                            </div>
                        ))
                    }
                    {Object.entries(assetAllocation).every(([asset, data]) => Math.abs(data.actual - data.target) <= 2) && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                            <span className="text-green-600">✅ Portfolio is well balanced - no rebalancing required</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderScenarioAnalysis = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Scenario Analysis</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Best Case */}
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <h4 className="font-semibold text-green-800 mb-3">📈 Best Case</h4>
                        <div className="space-y-2 text-sm">
                            <div><strong>Annual Return:</strong> 12.5%</div>
                            <div><strong>10-Year Balance:</strong> $3,240,000</div>
                            <div><strong>Probability:</strong> 10%</div>
                            <p className="text-xs text-green-700 mt-3">
                                Strong market performance, optimal asset allocation, minimal fees
                            </p>
                        </div>
                    </div>
                    
                    {/* Expected Case */}
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-3">📊 Expected Case</h4>
                        <div className="space-y-2 text-sm">
                            <div><strong>Annual Return:</strong> 7.5%</div>
                            <div><strong>10-Year Balance:</strong> $2,060,000</div>
                            <div><strong>Probability:</strong> 60%</div>
                            <p className="text-xs text-blue-700 mt-3">
                                Historical average returns, balanced market conditions
                            </p>
                        </div>
                    </div>
                    
                    {/* Worst Case */}
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <h4 className="font-semibold text-red-800 mb-3">📉 Worst Case</h4>
                        <div className="space-y-2 text-sm">
                            <div><strong>Annual Return:</strong> 2.8%</div>
                            <div><strong>10-Year Balance:</strong> $1,320,000</div>
                            <div><strong>Probability:</strong> 10%</div>
                            <p className="text-xs text-red-700 mt-3">
                                Market downturn, high volatility, recession scenario
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
    
    const renderTaxEfficiency = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Tax Efficiency Analysis</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="text-sm text-gray-600 mb-1">Tax Drag (Annual)</div>
                        <div className="text-3xl font-bold text-blue-700">0.85%</div>
                        <div className="text-xs text-gray-500 mt-1">Impact of taxes on returns</div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="text-sm text-gray-600 mb-1">Tax Efficiency Score</div>
                        <div className="text-3xl font-bold text-green-700">82/100</div>
                        <div className="text-xs text-gray-500 mt-1">Above average efficiency</div>
                    </div>
                </div>
                
                <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-medium mb-2">Tax Impact Breakdown</h4>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span>Capital Gains Tax (15%):</span>
                                <span className="font-medium text-red-600">-$4,500</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Dividend Tax (15%):</span>
                                <span className="font-medium text-red-600">-$2,800</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Franking Credits:</span>
                                <span className="font-medium text-green-600">+$3,200</span>
                            </div>
                            <div className="flex justify-between border-t pt-2">
                                <span><strong>Net Tax Impact:</strong></span>
                                <span className="font-bold text-red-600">-$4,100</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-2">💡 Tax Optimization Tips</h4>
                        <ul className="text-sm text-blue-700 space-y-1">
                            <li>• Utilize franking credits on Australian dividends</li>
                            <li>• Hold growth assets for 12+ months for CGT discount</li>
                            <li>• Consider switching to pension phase for 0% tax</li>
                            <li>• Harvest tax losses to offset capital gains</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
    
    const renderFeeAnalysis = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Fee Impact Analysis</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                        <div className="text-sm text-gray-600 mb-1">Annual Fees</div>
                        <div className="text-3xl font-bold text-red-700">$3,650</div>
                        <div className="text-xs text-gray-500 mt-1">Total annual cost</div>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                        <div className="text-sm text-gray-600 mb-1">Fee as % of Balance</div>
                        <div className="text-3xl font-bold text-orange-700">0.73%</div>
                        <div className="text-xs text-gray-500 mt-1">Annual fee ratio</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="text-sm text-gray-600 mb-1">20-Year Fee Impact</div>
                        <div className="text-3xl font-bold text-purple-700">$142,000</div>
                        <div className="text-xs text-gray-500 mt-1">Cumulative cost</div>
                    </div>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium mb-3">Fee Breakdown</h4>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span>Administration Fee:</span>
                            <span className="font-medium">$1,000/year</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Investment Fee (0.50%):</span>
                            <span className="font-medium">$2,500/year</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Transaction Costs (0.08%):</span>
                            <span className="font-medium">$150/year</span>
                        </div>
                        <div className="flex justify-between border-t pt-2">
                            <span><strong>Total:</strong></span>
                            <span className="font-bold">$3,650/year</span>
                        </div>
                    </div>
                </div>
                
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">Fee Impact on Returns</h4>
                    <p className="text-sm text-blue-700">
                        With a 7.5% gross return, fees reduce your net return to <strong>6.77%</strong>. 
                        Over 20 years, this represents approximately <strong>$142,000</strong> in foregone growth.
                    </p>
                </div>
            </div>
        </div>
    );

    const renderMonteCarlo = () => (
        <div className="space-y-6">
            {/* Monte Carlo Simulation */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Monte Carlo Simulation ({monteCarloResults.time_horizon} Years)</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">
                            {monteCarloResults.scenarios.toLocaleString()}
                        </div>
                        <div className="text-sm text-blue-700">Scenarios Run</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                            ${monteCarloResults.results['50th_percentile'].toLocaleString()}
                        </div>
                        <div className="text-sm text-green-700">Median Outcome</div>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                        <div className="text-2xl font-bold text-orange-600">
                            {monteCarloResults.results.probability_of_shortfall}%
                        </div>
                        <div className="text-sm text-orange-700">Shortfall Risk</div>
                    </div>
                </div>
            </div>

            {/* Percentile Outcomes */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Projected Outcomes (Percentiles)</h3>
                <div className="space-y-4">
                    {Object.entries(monteCarloResults.results)
                        .filter(([key, value]) => key.includes('percentile'))
                        .map(([percentile, value]) => (
                            <div key={percentile} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h4 className="font-semibold text-gray-800">
                                            {percentile.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                            {percentile === '50th_percentile' ? 'Most likely outcome' :
                                             percentile.includes('10th') || percentile.includes('25th') ? 'Conservative estimate' :
                                             'Optimistic estimate'}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-2xl font-bold text-blue-600">
                                            ${value.toLocaleString()}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {percentile === '50th_percentile' ? 'Median' :
                                             percentile.includes('10th') ? 'Worst 10%' :
                                             percentile.includes('25th') ? 'Worst 25%' :
                                             percentile.includes('75th') ? 'Best 25%' :
                                             'Best 10%'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    }
                </div>
            </div>

            {/* Risk Analysis */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Risk Analysis</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h4 className="font-semibold text-gray-700 mb-2">Downside Risk</h4>
                        <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                                <span>Probability of losing money:</span>
                                <span className="font-medium text-red-600">
                                    {monteCarloResults.results.probability_of_shortfall}%
                                </span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span>Worst case (10th percentile):</span>
                                <span className="font-medium text-red-600">
                                    ${monteCarloResults.results['10th_percentile'].toLocaleString()}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-700 mb-2">Upside Potential</h4>
                        <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                                <span>Best case (90th percentile):</span>
                                <span className="font-medium text-green-600">
                                    ${monteCarloResults.results['90th_percentile'].toLocaleString()}
                                </span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span>Probability of exceeding target:</span>
                                <span className="font-medium text-green-600">75%</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg shadow-inner">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Advanced Analytics</h2>
                <div className="flex space-x-2">
                    <select
                        value={timeframe}
                        onChange={(e) => setTimeframe(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="1Y">1 Year</option>
                        <option value="3Y">3 Years</option>
                        <option value="5Y">5 Years</option>
                    </select>
                    <button
                        onClick={() => setActiveView('performance')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeView === 'performance' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Performance
                    </button>
                    <button
                        onClick={() => setActiveView('risk')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeView === 'risk' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Risk Analysis
                    </button>
                    <button
                        onClick={() => setActiveView('allocation')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeView === 'allocation' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Asset Allocation
                    </button>
                    <button
                        onClick={() => setActiveView('monte_carlo')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeView === 'monte_carlo' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Monte Carlo
                    </button>
                    <button
                        onClick={() => setActiveView('scenarios')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeView === 'scenarios' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Scenario Analysis
                    </button>
                    <button
                        onClick={() => setActiveView('tax')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeView === 'tax' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Tax Efficiency
                    </button>
                    <button
                        onClick={() => setActiveView('fees')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeView === 'fees' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Fee Analysis
                    </button>
                </div>
            </div>

            {/* Content based on active view */}
            {activeView === 'performance' && renderPerformanceBenchmarking()}
            {activeView === 'risk' && renderRiskAnalysis()}
            {activeView === 'allocation' && renderAssetAllocation()}
            {activeView === 'monte_carlo' && renderMonteCarlo()}
            {activeView === 'scenarios' && renderScenarioAnalysis()}
            {activeView === 'tax' && renderTaxEfficiency()}
            {activeView === 'fees' && renderFeeAnalysis()}

            {/* Action Buttons */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        Analysis as of: {new Date().toLocaleDateString()}
                    </div>
                    <div className="flex space-x-4">
                        <button className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
                            Export Analytics Report
                        </button>
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                            Refresh Analysis
                        </button>
                    </div>
                </div>
            </div>

            {/* Asset Allocation Modal */}
            {showAssetAllocationModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-semibold">Edit Asset Allocation</h3>
                            <button
                                onClick={() => setShowAssetAllocationModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* Target Allocation */}
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-4">Target Allocation (%)</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {Object.entries(assetAllocation).map(([asset, data]) => (
                                        <div key={asset} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                            <label className="font-medium text-gray-700">
                                                {asset.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </label>
                                            <input
                                                {...getNumericInputProps()}
                                                min="0"
                                                max="100"
                                                step="0.1"
                                                value={data.target}
                                                onChange={(e) => {
                                                    const newTarget = parseFloat(e.target.value) || 0;
                                                    setSmsfState(prev => ({
                                                        ...prev,
                                                        assetAllocationTargets: {
                                                            ...prev.assetAllocationTargets,
                                                            [asset]: newTarget
                                                        }
                                                    }));
                                                }}
                                                className="w-20 px-2 py-1 border border-gray-300 rounded text-right"
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                                    <div className="text-sm text-blue-700">
                                        Total Target: {Object.values(assetAllocation).reduce((sum, asset) => sum + (asset.target || 0), 0).toFixed(1)}%
                                    </div>
                                </div>
                            </div>

                            {/* Asset Values */}
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-4">Current Asset Values ($)</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {Object.entries(assetAllocation).map(([asset, data]) => (
                                        <div key={asset} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                            <label className="font-medium text-gray-700">
                                                {asset.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </label>
                                            <input
                                                {...getNumericInputProps()}
                                                min="0"
                                                step="1000"
                                                value={data.value}
                                                onChange={(e) => {
                                                    const newValue = parseFloat(e.target.value) || 0;
                                                    setSmsfState(prev => ({
                                                        ...prev,
                                                        assetValues: {
                                                            ...prev.assetValues,
                                                            [asset]: newValue
                                                        }
                                                    }));
                                                }}
                                                className="w-32 px-2 py-1 border border-gray-300 rounded text-right"
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-4 p-3 bg-green-50 rounded-lg">
                                    <div className="text-sm text-green-700">
                                        Total Portfolio Value: ${Object.values(assetAllocation).reduce((sum, asset) => sum + (asset.value || 0), 0).toLocaleString()}
                                    </div>
                                </div>
                            </div>

                            {/* Performance Returns */}
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-4">Annual Performance (%)</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {Object.entries(assetAllocation).map(([asset, data]) => (
                                        <div key={asset} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                            <label className="font-medium text-gray-700">
                                                {asset.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </label>
                                            <input
                                                {...getNumericInputProps()}
                                                step="0.1"
                                                value={data.performance}
                                                onChange={(e) => {
                                                    const newPerformance = parseFloat(e.target.value) || 0;
                                                    setSmsfState(prev => ({
                                                        ...prev,
                                                        assetPerformance: {
                                                            ...prev.assetPerformance,
                                                            [asset]: newPerformance
                                                        }
                                                    }));
                                                }}
                                                className="w-20 px-2 py-1 border border-gray-300 rounded text-right"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex justify-end space-x-4 pt-4 border-t">
                                <button
                                    onClick={() => setShowAssetAllocationModal(false)}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        setShowAssetAllocationModal(false);
                                        // Trigger recalculation
                                        window.location.reload();
                                    }}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SMSFAdvancedAnalytics;
